import { filter } from "@prismicio/client"
import type { GetStaticPaths, GetStaticPropsResult } from "next"

import { isSlug, trimText } from "@bounce/util"

import { getTopCities } from "../../libs/bounce/cities"
import { getBlogPost, getBlogPosts } from "../../libs/prismic/blogPost"
import { createClient } from "../../libs/prismic/createClient"
import type { ArticleScreenProps } from "../../screens/Generic/ArticleScreen/ArticleScreen"
import ArticleScreen from "../../screens/Generic/ArticleScreen/ArticleScreen"
import { enhanceStaticProps, notFound } from "../../utils/getStaticProps"
import { loadTranslations } from "../../utils/i18n/server"

const RELATED_ARTICLES_LIMIT = 3

export const getStaticPaths: GetStaticPaths<{ article: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  }
}

export const getStaticProps = enhanceStaticProps<ArticleScreenProps, { article: string }>(
  async ({
    params,
    previewData,
    localeData: { locale, language },
  }): Promise<GetStaticPropsResult<ArticleScreenProps>> => {
    const articleUID = params?.article
    if (!isSlug(articleUID)) {
      return notFound()
    }

    const prismicClient = createClient({ previewData })
    const [translations, topCities, article] = await Promise.all([
      loadTranslations({ language, namespaces: ["marketing-generic"] }),
      getTopCities({ prismicClient, language, kind: "luggage" }),
      getBlogPost({ prismicClient, uid: articleUID, params: { lang: locale } }),
    ])

    // Return 404 if no articles exist
    if (!article) {
      return notFound()
    }

    const relatedArticles = await getBlogPosts({
      prismicClient,
      params: {
        lang: locale,
        limit: RELATED_ARTICLES_LIMIT,
        filters: [filter.not("my.blog_post.uid", articleUID)],
      },
    })

    article.metaTitle = article.metaTitle || `${article.title} - Bounce`
    article.metaDescription = article.metaDescription || trimText(article.excerpt, 170)

    return {
      props: {
        topCities,
        article,
        relatedArticles,
        type: "blog_post",
        languages: [language],
        ...translations,
      },
      revalidate: 60 * 60 * 5, // 5 hours
    }
  },
)

export default ArticleScreen
